import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { UseService } from "../../../services/Profile/UseService";

interface MessageBalance {
  total_messages: number;
  messages_used: number;
  current_month_messages: number;
  closing_date: string;
}

interface ContextProps {
  availableUsers: any[];
  dependents: any[];
  messageBalance: MessageBalance | null;
  userQuantity: number | null;
  isLoading: boolean;
  addUserToPlan: (partnerId: string | number) => Promise<[any, any]>;
  toggleUserRemoval: (userId: string | number, pendingRemoval: boolean) => Promise<any[]>;
  refreshDependents: () => Promise<void>;
}

const UseContext = createContext<ContextProps>({} as ContextProps);

export const UseProvider = ({ children }: { children: ReactNode }) => {
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [dependents, setDependents] = useState<any[]>([]);
  const [messageBalance, setMessageBalance] = useState<MessageBalance | null>(null);
  const [userQuantity, setUserQuantity] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const service = new UseService();

  const fetchDependents = async () => {
    const [response, error] = await service.listDependents();
    if (!error) setDependents(response?.data?.items ?? []);
  };

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      const [response, error] = await service.listAvailableUsers();
      if (!error) setAvailableUsers(response?.data || []);
      const [balanceResponse, balanceError] = await service.getMessageBalance();
      if (!balanceError) setMessageBalance(balanceResponse?.data ?? null);
      const [subscriptionResponse, subscriptionError] = await service.getActiveSubscription();
      if (!subscriptionError) setUserQuantity(subscriptionResponse?.data?.user_quantity ?? null);
      await fetchDependents();
      setIsLoading(false);
    };
    fetchUsers();
  }, []);

  const addUserToPlan = async (partnerId: string | number): Promise<[any, any]> => {
    const [response, error] = await service.addUserToPlan(partnerId);
    return [response, error];
  };

  const toggleUserRemoval = async (userId: string | number, pendingRemoval: boolean) => {
    const [response, error] = pendingRemoval
      ? await service.cancelRemoval(userId)
      : await service.removeUser(userId);
    if (!error) {
      setDependents((prev) =>
        prev.map((d) => d.id === Number(userId) ? { ...d, pending_removal: !pendingRemoval } : d)
      );
    }
    return [response, error];
  };

  const refreshDependents = async () => {
    await fetchDependents();
  };

  return (
    <UseContext.Provider value={{ availableUsers, dependents, messageBalance, userQuantity, isLoading, addUserToPlan, toggleUserRemoval, refreshDependents }}>
      {children}
    </UseContext.Provider>
  );
};

export const useUse = () => useContext(UseContext);
